import React, { useState, useEffect, useMemo, useCallback } from 'react';
import type { ScriptType, ApiResponse } from '@types';
import useIntentApi from '../hooks/useIntentApi';
import { useSessionStore } from '@features/auth/store/useSessionStore';
import { usePaginationData } from '@hooks/usePaginationData';

import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@core-ui/Components/ui/table';
import {
  Card,
  CardContent, CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@core-ui/Components/ui/card';
import { Button } from '@core-ui/Components/ui/button';
import { Pagination } from '@core-ui/Components/ui/pagination';
import { SearchInput } from '@ui/shared/searchInput';
import { PaginationControls } from '@ui/shared/paginationControls';
import { EmptyState } from '@ui/shared/emptyState';
import { TableSkeletonRows } from '../components/SkeletonsRows';
import { IntentFormDialog } from '../components/IntentForm';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';

import { formatDate } from '@utils/date';
import { useTranslation } from 'react-i18next';
import { CirclePlus, Pencil, Trash2 } from 'lucide-react';
import { useFeatureFlagsStore } from '@store/featureFlags';

const ROWS_PER_PAGE_OPTIONS = [5, 10, 15, 20];

const IntentCrud: React.FC = () => {
  const { t } = useTranslation();
  const loadFeatureFlags = useFeatureFlagsStore((state) => state.loadFeatureFlags);

  const featureFlags = useFeatureFlagsStore((s) => s.flags);

  const currentUser = useSessionStore((state) => state.currentUser);
  const { getScriptsByOrgId, deleteScript } = useIntentApi();

  const [search, setSearch] = useState('');
  const [scripts, setScripts] = useState<ScriptType[]>([]);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'view' | null>(
    null,
  );
  const [selectedIntent, setSelectedIntent] = useState<ScriptType | null>(null);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(false);

  const orgId = currentUser?.organizationId ?? null;

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (orgId == null) {
        return;
      }
      setLoading(true);
      const result = await getScriptsByOrgId(orgId);
      if (result.success && result.data != null) {
        setScripts(result.data);
      }
      setLoading(false);
    };

    void fetchData();
    void loadFeatureFlags(currentUser?.organizationId);
  }, [orgId]);

  const refreshScripts = useCallback(async (): Promise<void> => {
    if (orgId == null) {
      return;
    }
    setLoading(true);
    const result = await getScriptsByOrgId(orgId);
    if (result.success && result.data != null) {
      setScripts(result.data);
    }
    setLoading(false);
  }, [orgId, getScriptsByOrgId]);

  const handleDeleteIntent = useCallback(async (): Promise<
    ApiResponse<null>
  > => {
    if (selectedIntent?.id == null) {
      return {
        success: false,
        error: { message: 'Invalid ID', raw: 'Missing ID' },
      };
    }
    const result = await deleteScript(selectedIntent.id);
    if (result.success) {
      await refreshScripts();
    }
    return result;
  }, [selectedIntent, deleteScript, refreshScripts]);

  const openDialog = (
    mode: 'create' | 'edit' | 'view',
    intent?: ScriptType,
  ): void => {
    setSelectedIntent(intent ?? null);
    setFormMode(mode);
    setShowFormDialog(true);
  };

  const openDeleteDialog = (intent: ScriptType): void => {
    setSelectedIntent(intent);
    setShowDeleteDialog(true);
  };

  const filteredData = useMemo(
    () =>
      scripts.filter(
        (item) =>
          item.title.toLowerCase().includes(search.toLowerCase()) ||
          item.body.toLowerCase().includes(search.toLowerCase()),
      ),
    [scripts, search],
  );

  const {
    currentPage, setCurrentPage,
    rowsPerPage, setRowsPerPage,
    currentData,
    totalItems,
  } = usePaginationData<ScriptType>({ mode: 'client', data: filteredData, initialRowsPerPage: 10 });

  return (
    <div>
      <Card className="bg-card border border-border rounded-md shadow-sm p-4">
        <CardHeader className="px-1">
          <CardTitle data-qa-id="intent-title" className="text-2xl font-bold">
            {t('intent.title')}
          </CardTitle>
          <CardDescription className="mt-1">
            {t('intent.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-6 w-full">
            <div className="w-full ml-1">
              <SearchInput
                aria-label="Search intent"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                onClear={() => {
                  setSearch('');
                }}
                placeholder={t('intent.Search intent')}
              />
            </div>
            <Button
              data-qa-id="new-intent-button"
              onClick={() => {
                openDialog('create');
              }}
              className="bg-primary text-primary-foreground w-full md:w-auto cursor-pointer"
            >
              <CirclePlus className="mr-2 h-4 w-4" />
              {t('intent.New Intent')}
            </Button>
          </div>

          <div className="rounded-md border border-border">
            <Table className="min-w-full border-collapse">
              <TableHeader>
                <TableRow className="bg-muted/60 border-b border-border/60">
                  <TableHead className="text-left">
                    {t('intent.Name')}
                  </TableHead>
                  <TableHead className="text-center">
                    {t('intent.Content')}
                  </TableHead>
                  <TableHead className="text-center">
                    {t('intent.Last Modified')}
                  </TableHead>
                  <TableHead className="text-center">
                    {t('intent.Actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableSkeletonRows rows={rowsPerPage} />
                ) : filteredData.length === 0 ? (
                  <TableRow data-qa-id="intent-not-found">
                    <TableCell
                      colSpan={4}
                      className="text-center py-12 hover:bg-muted/10"
                    >
                      <EmptyState message={t('intent.No found')} />
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {currentData.map((intent) => (
                      <TableRow
                        key={intent.id}
                        className="border-b border-border hover:bg-muted/30"
                      >
                        <TableCell className="whitespace-pre-wrap break-words max-w-xs">
                          {intent.title}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600 dark:text-gray-400 max-w-xl">
                          <div data-qa-id="intent-content" className="line-clamp-4 whitespace-pre-wrap font-mono text-xs">
                            {intent.body}
                          </div>
                          {intent.body.split('\n').length > 4 && (
                            <button
                              onClick={() => {
                                openDialog('view', intent);
                              }}
                              className="text-xs text-primary hover:underline text-left w-fit cursor-pointer"
                              aria-label="View more"
                            >
                              {t('intent.View more')}
                            </button>
                          )}
                        </TableCell>
                        <TableCell className={'text-center'}>
                          {formatDate(intent.updatedAt)}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center">
                            <Button
                              variant="ghost"
                              onClick={() => {
                                openDialog('edit', intent);
                              }}
                              aria-label="Edit intent"
                              className="p-0 m-0 h-6 w-6 flex items-center justify-center cursor-pointer"
                            >
                              <Pencil className="w-4 h-4 text-primary" />
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => {
                                openDeleteDialog(intent);
                              }}
                              aria-label="Delete intent"
                              className="p-0 m-0 h-6 w-6 flex items-center justify-center cursor-pointer"
                            >
                              <Trash2 className="w-4 h-4 text-error" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>

        {filteredData.length > 0 && (
          <CardFooter className="flex items-center justify-between gap-4">
            <Pagination className="w-full">
              <PaginationControls
                totalItems={totalItems}
                currentPage={currentPage}
                onPageChange={(p) => {
                  setCurrentPage(Math.max(1, Math.min(p, Math.ceil(totalItems / rowsPerPage))));
                }}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={(n) => {
                  setRowsPerPage(n); setCurrentPage(1);
                }}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              />
            </Pagination>
          </CardFooter>
        )}
      </Card>

      {showFormDialog && formMode != null && (
        <IntentFormDialog
          open={showFormDialog}
          onOpenChange={setShowFormDialog}
          mode={formMode}
          intent={selectedIntent}
          orgId={orgId}
          onSave={async () => {
            await refreshScripts();
            setShowFormDialog(false);
          }}
          aiScriptFeatureFlag={featureFlags.aiScripts}
        />
      )}

      {showDeleteDialog && selectedIntent != null && (
        <ConfirmDeleteDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          intent={selectedIntent}
          onDelete={handleDeleteIntent}
        />
      )}
    </div>
  );
};

export default IntentCrud;
